import { useDarkMode } from "lib/use-dark-mode"
import { useMemo } from "react"
import { useDialog } from "lib/@hooks/useDialog"
import {
    AMENDS_NOTE_MODE_SPLIT,
    AMENDS_NOTE_MODE_UNIFIED,
    buildAudienceAmendsPayload,
    hasAudienceAmendsContent,
    normalizeAudienceAmends,
} from "../lib/audience-aware-amends.mjs"
import {
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    Typography,
} from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { MacroExpander } from "authoring/routes/macro/components/macro-expander"
import { UserAvatar } from "minimals-template/components/user-avatar"
import { AmendsEditorWithMacros } from "./amends-editor-with-macros"
import { Else } from "lib/@components/else"
import { sanitizeAmendsHtml } from "../lib/sanitize-amends-html"
import { hasAmendsContent } from "../lib/amends-content"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { ChooseAuthorNoteDialog } from "./choose-author-note-dialog"
import { createSlot } from "lib/@components/slot/create-slot"
import { AmendsEditActionButton } from "./amends-edit-action-button"

export const AmendsEditAction = createSlot("Authoring.AmendsEditAction")
AmendsEditAction.plug(<AmendsEditActionButton priority={100} />)
const KEEP_INTERNAL = "Internal"

export function AudienceAwareAmendsEditor({
    subtitle,
    editMode = true,
    allowSplit = true,
    required = true,
    noteData,
    macroMap,
    onChange,
    onCancel,
    onSave,
    onEdit,
    onDelete,
    showDelete = false,
    canEdit = true,
    authorName,
    savedAt,
    busy = false,
    maxWidth = 760,
    saveCaption = "Save",
    cancelCaption = "Cancel",
    autoFocusOnOpen = true,
    isCollapsed = false,
    onToggleCollapsed,
    collapsible = false,
    hideActions = false,
}) {
    const isDark = useDarkMode()
    const chooseAuthorNote = useDialog(<ChooseAuthorNoteDialog />, { maxWidth: "sm", fullWidth: true })
    const normalizedNote = useMemo(getNormalizedNote, [noteData, allowSplit])
    const savedAtLabel = formatSavedAt(savedAt)
    const hasAnyContent = hasAudienceAmendsContent(normalizedNote)
    const saveDisabled = !canEdit || !hasAnyContent || busy
    let subtitleColor = "text.secondary"
    if (isDark) subtitleColor = "#b4b4b4"

    if (!editMode) {
        return (
            <Box sx={{ backgroundColor: "action.hover", borderRadius: 2, p: 2 }} data-cy="amends-readonly-card">
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify icon="mdi:message-text-outline" width={20} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Author Notes
                        </Typography>
                        <If value={normalizedNote.isSplit}>
                            <Chip label="Split" size="small" color="info" variant="outlined" />
                        </If>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AmendsEditAction.Slot onEdit={onEdit} canEdit={canEdit} />
                        <If truthy={!!collapsible}>
                            <IconButton
                                size="small"
                                onClick={onToggleCollapsed}
                                aria-label={isCollapsed ? "Expand author note" : "Collapse author note"}
                                data-cy="amends-collapse-button"
                            >
                                <Iconify
                                    icon={isCollapsed ? "ic:round-keyboard-arrow-down" : "ic:round-keyboard-arrow-up"}
                                    width={18}
                                    height={18}
                                />
                            </IconButton>
                        </If>
                    </Stack>
                </Stack>
                <Collapse in={!isCollapsed}>
                    <Box mt={1.5}>
                        <MacroExpander>
                            <If value={normalizedNote.isSplit}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Internal
                                </Typography>
                                {renderReadonlyContent(normalizedNote.internalContent)}
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    External
                                </Typography>
                                {renderReadonlyContent(normalizedNote.externalContent)}
                            </If>
                            <If value={!normalizedNote.isSplit}>{renderReadonlyContent(normalizedNote.content)}</If>
                        </MacroExpander>
                    </Box>
                    <If truthy={authorName || savedAtLabel}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
                            <If truthy={authorName}>
                                <UserAvatar
                                    user={{ displayName: authorName }}
                                    data-cy="amends-author-avatar"
                                    sx={{
                                        width: 22,
                                        height: 22,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        "& .MuiTypography-root": {
                                            fontSize: 11,
                                            lineHeight: 1,
                                            textAlign: "center",
                                            transform: "translateY(1px)",
                                        },
                                    }}
                                />
                            </If>
                            <Typography variant="body2" sx={{ color: "text.secondary" }} data-cy="amends-meta">
                                {[authorName, savedAtLabel].filter(Boolean).join(", ")}
                            </Typography>
                        </Stack>
                    </If>
                </Collapse>
            </Box>
        )
    }

    return (
        <Box data-cy="amends-editor">
            <Box data-cy="audience-aware-amends-editor">
                <If truthy={subtitle}>
                    <Typography variant="caption" sx={{ color: subtitleColor }}>
                        {subtitle}
                    </Typography>
                </If>
                <Box mt={1.5}>
                    <If value={allowSplit}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={normalizedNote.isSplit}
                                    onChange={handleToggleSplit}
                                    disabled={busy || !canEdit}
                                    data-cy="amends-split-switch"
                                />
                            }
                            label="Use different notes for internal and external viewers"
                        />
                    </If>
                </Box>
                <If value={normalizedNote.isSplit}>
                    <Box mt={1} sx={{ width: 1, maxWidth }}>
                        <AmendsEditorWithMacros
                            macroMap={macroMap}
                            editMode
                            value={normalizedNote.internalContent}
                            onChange={handleInternalChange}
                            title="Internal"
                            hideActions
                            required={false}
                            autoFocusOnOpen={autoFocusOnOpen}
                            dataCy="audience-aware-amends-input"
                        />
                    </Box>
                    <Box mt={1.5} sx={{ width: 1, maxWidth }}>
                        <AmendsEditorWithMacros
                            macroMap={macroMap}
                            editMode
                            value={normalizedNote.externalContent}
                            onChange={handleExternalChange}
                            title="External"
                            hideActions
                            required={false}
                            autoFocusOnOpen={false}
                            dataCy="audience-aware-amends-input"
                        />
                    </Box>
                </If>
                <If value={!normalizedNote.isSplit}>
                    <Box mt={1} sx={{ width: 1, maxWidth }}>
                        <AmendsEditorWithMacros
                            macroMap={macroMap}
                            editMode
                            value={normalizedNote.content}
                            onChange={handleUnifiedChange}
                            title="Author Notes"
                            hideActions
                            required={required}
                            autoFocusOnOpen={autoFocusOnOpen}
                            dataCy="audience-aware-amends-input"
                        />
                    </Box>
                </If>

                <If truthy={!hideActions}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1.5}>
                        <If truthy={showDelete}>
                            <Button
                                onClick={onDelete}
                                variant="text"
                                color="error"
                                startIcon={<Iconify icon="mdi:delete" />}
                                data-cy="amends-delete-button"
                                disabled={busy || !canEdit}
                            >
                                Delete
                            </Button>
                            <Else>
                                <Button
                                    onClick={onCancel}
                                    variant="text"
                                    color="inherit"
                                    startIcon={<Iconify icon="mdi:close-circle-outline" />}
                                    data-cy="amends-cancel-button"
                                    disabled={busy}
                                >
                                    {cancelCaption}
                                </Button>
                            </Else>
                        </If>

                        <Button
                            onClick={onSave}
                            variant="contained"
                            startIcon={<Iconify icon="mdi:check" />}
                            data-cy="amends-save-button"
                            disabled={saveDisabled}
                        >
                            {saveCaption}
                        </Button>
                    </Stack>
                </If>
            </Box>
        </Box>
    )

    function getNormalizedNote() {
        const nextNote = normalizeAudienceAmends(noteData)
        if (allowSplit) return nextNote

        return buildAudienceAmendsPayload({
            content: nextNote.content,
            internalContent: nextNote.content,
            externalContent: nextNote.content,
            noteMode: AMENDS_NOTE_MODE_UNIFIED,
        })
    }

    function handleUnifiedChange(content) {
        onChange?.(
            buildAudienceAmendsPayload({
                content,
                internalContent: content,
                externalContent: content,
                noteMode: AMENDS_NOTE_MODE_UNIFIED,
            })
        )
    }

    function handleInternalChange(internalContent) {
        onChange?.(
            buildAudienceAmendsPayload({
                ...normalizedNote,
                internalContent,
                noteMode: AMENDS_NOTE_MODE_SPLIT,
            })
        )
    }

    function handleExternalChange(externalContent) {
        onChange?.(
            buildAudienceAmendsPayload({
                ...normalizedNote,
                externalContent,
                noteMode: AMENDS_NOTE_MODE_SPLIT,
            })
        )
    }

    async function handleToggleSplit(_, checked) {
        if (checked) {
            onChange?.(
                buildAudienceAmendsPayload({
                    content: normalizedNote.content,
                    internalContent: normalizedNote.content,
                    externalContent: normalizedNote.content,
                    noteMode: AMENDS_NOTE_MODE_SPLIT,
                })
            )
            return
        }

        if (normalizedNote.internalContent.trim() === normalizedNote.externalContent.trim()) {
            handleUnifiedChange(normalizedNote.internalContent)
            return
        }

        const selection = await chooseAuthorNote()

        if (!selection) return

        const content = selection === KEEP_INTERNAL ? normalizedNote.internalContent : normalizedNote.externalContent
        handleUnifiedChange(content)
    }
}

function renderReadonlyContent(content) {
    const safeReadonlyHtml = sanitizeAmendsHtml(content)

    if (!hasAmendsContent(content)) {
        return (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No content
            </Typography>
        )
    }

    return (
        <Box
            data-cy="amends-readonly-content"
            sx={{ "& p": { m: 0 }, "& p + p": { mt: 1 } }}
            dangerouslySetInnerHTML={{ __html: safeReadonlyHtml }}
        />
    )
}

function formatSavedAt(savedAt) {
    if (!savedAt) return null
    const date = new Date(savedAt)
    if (Number.isNaN(date.getTime())) return savedAt
    return date.format(DATE_FORMATS.long)
}
